import useQuestionnaire from 'hooks/useQuestionnaire';
import { useEffect, useState } from 'react';
import { useQuestionnaireStore } from 'store/questionnaire';
import QuestionnaireContainer from './components/QuestionnaireContainer';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { motion } from 'framer-motion';
import { checkSpanishLocale, cn } from 'lib/utils';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function Surgery() {
  const { t } = useTranslation();
  const questionData = useQuestionnaireStore((store) => store.questionData);
  const isSpanish = checkSpanishLocale();

  const { onSubmit, isLoading } = useQuestionnaire();

  const [surgeries, setSurgeries] = useState<string[]>([]);
  const [newSurgery, setNewSurgery] = useState('');

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const userChoice =
        questionData?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];
      if (userChoice === 'No') {
        setSurgeries([]);
        return;
      }

      const answer = userChoice?.split(', ');
      setSurgeries(answer?.length ? answer?.map((item) => item.trim()) : []);
    }
  }, [isSpanish, questionData]);

  const onAdd = () => {
    setSurgeries([...surgeries, newSurgery]);
    setNewSurgery('');
  };

  const onDelete = (surgery: string) => {
    setSurgeries(surgeries.filter((c) => c !== surgery));
  };

  const onClearAll = () => setSurgeries([]);

  const onContinue = async () => {
    let choiceValue = '';
    if (surgeries.length > 0) {
      choiceValue = surgeries.join(', ');
    }
    onSubmit({
      choice_value: choiceValue || 'No',
      spanish_choice_value: choiceValue || 'No',
      questionData,
    });
  };

  return (
    <QuestionnaireContainer
      questionData={questionData}
      isLoading={isLoading}
      onContinue={onContinue}
      questionNumber={13}
    >
      <div className='flex items-center gap-4'>
        <Input
          className='w-full md:w-1/2 h-9'
          value={newSurgery}
          onChange={(e) => setNewSurgery(e.target.value)}
          placeholder={t('add_surgery')}
        />
        <Button onClick={onAdd} className='px-6 h-9'>
          {t('add')}
        </Button>
      </div>

      <div className='mt-4'>
        <div className='flex items-center justify-between'>
          <h1 className='font-semibold'>{t('surgeries_added')}</h1>
          <Button
            variant='link'
            className='text-sm underline transition-all duration-300 hover:no-underline text-rose-500 h-9'
            onClick={onClearAll}
            disabled={!surgeries.length}
            size='sm'
          >
            {t('clear_all')}
          </Button>
        </div>
        <div className='flex flex-wrap gap-2'>
          {surgeries?.length ? (
            surgeries.map((surgery) => {
              return (
                <motion.div
                  key={surgery}
                  className={cn(
                    'border border-gray-300 text-sm font-medium text-[#656D76] px-4 rounded-xl h-8 flex items-center justify-between cursor-pointer transition-all duration-150 gap-1.5'
                  )}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => onDelete(surgery)}
                >
                  <p className='leading-5'>{surgery}</p>
                  <X className='w-4 h-4' />
                </motion.div>
              );
            })
          ) : (
            <div
              className={cn(
                'bg-[#F3F4F6] text-sm font-medium text-[#656D76] px-4 rounded-xl h-8 flex items-center justify-center cursor-pointer transition-all duration-150'
              )}
            >
              <p className='leading-5'>{t('none')}</p>
            </div>
          )}
        </div>
      </div>
    </QuestionnaireContainer>
  );
}
